 
import React from "react";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import { StandadCard } from "../../../../../../components/Cards";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { LoadState, MODULE_IDS } from "../../../../../../constants/enums";
import { useDispatchWrapper,
  useBreadcrumb,
} from "../../../../../../hooks";
import { useFormik } from "formik";
import {
  Box,
  Checkbox,
  Grid,
  Stack,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../components/formsComponents";
import { CustomDatePicker } from "../../../../../../components/formsComponents/CustomDatePicker";
import moment from "moment";

import {
  addBusinesPolicyEquipmentnAsync,
  addLeadBusinessEquipmentAsync,
  clearBusinessEquipment,
  fetchBusinessPolicyEquipmentAsync,
  fetchLeadEquipmentAsync,
} from "../../../../../../redux/businessPolicy/businessPolicyEquipment/businessPolicyEquipmentActions";
import { useNavigate } from "react-router-dom";
import {
  ILocationResponsePayload,
  LocationAutoComplete,
} from "../../../../../../components/LocationAutoComplete/LocationAutoComplete";
import { fetchBrokerageIntrestedAllPartiesAsync } from "../../../../../../redux/InterestedParties/IntrestedPartiesActions";
import { CurrencyTextField } from "../../../../../../components/CurrencyTextField/CurrencyTextField";
import { isAlphaNumericCapital } from "../../../../../../helpers/isAlphaNumericCapital";
import { RoleBasedCustomButton } from "../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { InterestedPartyCodeSearchSelect } from "../../../../../../components/AutoCompleteSearches/InterestedPartyCodeSearchSelect";
import { IBusinessPolicyInterested } from "../../../../../../redux/businessPolicy/businessPolicyInterested/businessPolicyInterested.types";
import { COMMON_STATUS } from "../../../../../../constants/constants";
import { IBusinessPolicyEquipment } from "../../../../../../redux/businessPolicy/businessPolicyEquipment/businessPolicyEquipment.types";
import { FieldSet } from "../../../../../../components/FieldSet/FieldSet";
import { PageContainer } from "../../../../../../components/container/PageContainer";

interface EquipmentProps {
  customerId?: number;
  customerPolicyId?: number;
  /** Lead-scoped identifier. When present, the form saves/fetches by lead_id. */
  leadId?: string;
  equipmentNumber?: number;
  readOnly?: boolean;
  fromRightPanel?: boolean;
  staticStatusValue?: string;
  created_from?: string;
  onSaveSuccess: (equipment: IBusinessPolicyEquipment) => void;
  initialData?: IBusinessPolicyEquipment;
  disableApiCalls?: boolean;
  disableSaveApi?: boolean;
}
export const Equipment: React.FC<EquipmentProps> = (props) => {
  const {
    customerId,
    customerPolicyId,
    leadId,
    equipmentNumber,
    onSaveSuccess,
    readOnly = false,
    fromRightPanel = false,
    staticStatusValue,
    created_from,
    initialData,
    disableApiCalls = false,
    disableSaveApi = false,
  } = props;

  useBreadcrumb({
    title: "",
    items: [
    {
      to: "/policies/",
      title: "all-policies ",
    },
    {
      to: "/policies/Business%20Policy",
      title: "business",
    },
    {
      to: "/customer-overview/" + customerId,
      title: "customer",
    },
    {
      to:
        "/business-policy/" +
        customerId +
        "/schedule-tab/" +
        customerPolicyId +
        `?tab=0`,
      title: "equipments",
    },
    {
      title: equipmentNumber ? "edit-equipment" : "add-equipment",
    },
  ],
  });


  const { equipmentLoading, equipment, error } = useSelector(
    (storeState: IStoreState) => storeState.business.equipment
  );
  const [saveLoading, setSaveLoading] = React.useState(false);
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const {
    values,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
    errors,
  } = useFormik({
    initialValues: initialData ? initialData : equipment,
    validate: (values) => {},
    onSubmit: async (values) => {
      if (disableSaveApi) {
        onSaveSuccess(values);
      } else {
        let statusValue = values.status;
        if (staticStatusValue) {
          statusValue = staticStatusValue;
        }
        // Lead-binder scope: save by lead_id (no customer_policy_id required).
        if (leadId) {
          setSaveLoading(true);
          dispatch(
            addLeadBusinessEquipmentAsync(
              {
                ...values,
                status: statusValue,
                created_from: created_from || "Equipment",
              },
              { leadId },
              (isSuccess, equipment) => {
                if (isSuccess && equipment) {
                  window.scrollTo(0, 0);
                  onSaveSuccess(equipment);
                }
                setSaveLoading(false);
              }
            )
          );
          return;
        }
        if (customerPolicyId && customerId) {
          setSaveLoading(true);
          dispatch(
            addBusinesPolicyEquipmentnAsync(
              {
                ...values,
                customer_id: customerId,
                customer_policy_id: customerPolicyId,
                status: statusValue,
                created_from: created_from || "Equipment",
              },
              (isSuccess, equipment) => {
                if (isSuccess && equipment) {
                  window.scrollTo(0, 0);
                  onSaveSuccess(equipment);
                  setSaveLoading(false);
                }
              }
            )
          );
        }
      }
    },
  });

  const handleAddress = (data: ILocationResponsePayload) => {
    setValues({
      ...values,
      address: data.address,
      city: data.city,
      country: data.country,
      province_or_state: data.state,
      postal_code: data.postalCode,
    });
  };
  const handleSerialNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    const sanitizedValue = isAlphaNumericCapital(value);
    setFieldValue(name, sanitizedValue);
  };

  React.useEffect(() => {
    if (disableApiCalls || !equipmentNumber) {
      return;
    }
    if (leadId) {
      dispatch(fetchLeadEquipmentAsync({ leadId }, equipmentNumber));
    } else if (customerPolicyId) {
      dispatch(
        fetchBusinessPolicyEquipmentAsync(customerPolicyId, equipmentNumber)
      );
    }
  }, [customerPolicyId, leadId, equipmentNumber]);

  React.useEffect(() => {
    if (initialData && disableApiCalls) {
      return;
    }
    setValues({
      ...equipment,
      incremental_number: equipmentNumber || null,
      effective_date: moment().format("YYYY-MM-DD"),
    });
  }, [equipment]);

  React.useEffect(() => {
    return () => {
      dispatch(clearBusinessEquipment());
    };
  }, []);

  React.useEffect(() => {
    if (customerPolicyId) {
      dispatch(fetchBrokerageIntrestedAllPartiesAsync());
    }
  }, []);

  const handleIntrestedPartyCode = (data: IBusinessPolicyInterested) => {
    if (data) {
      setValues({
        ...values,
        interest: data.interest || "",
        interested_party_code: data.interested_party_code || "",
        interested_party: data.interested_party || "",
        address: data.address || "",
        unit_or_suite: data.unit_or_suite || "",
        city: data.city || "",
        country: data.country || "",
        province_or_state: data.province_or_state || "",
        postal_code: data.postal_code || "",
      });
    }
  };

  const intrestedParities = useSelector(
    (storeState: IStoreState) =>
      storeState.borkerage.intrestedParties.intrestedParties.list
  );

  const intrestedParitiesDropdown = React.useMemo(() => {
    return intrestedParities.map((item) => {
      return {
        label: item.interested_party_code,
        value: item.interested_party_code || "",
      };
    });
  }, [intrestedParities]);

  return (
 <PageContainer
      title={equipmentNumber ? "edit-equipment" : "add-equipment"}
      description="this is inner page"
    >    <PageLoader
      loading={equipmentLoading === LoadState.InProgress}
      error={error ? { message: error } : undefined}
    >
      <StandadCard
        sx={{ mt: 2 }}
        renderWithoutCard={fromRightPanel}
        heading={!fromRightPanel ? "Equipment" : ""}
      >
        <form onSubmit={handleSubmit}>
          <FieldSet disabled={readOnly}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={1}>
                <CustomFormLabel>#</CustomFormLabel>
                <CustomTextField
                  name="incremental_number"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.incremental_number}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <CustomFormLabel>Effective Date</CustomFormLabel>
                <CustomDatePicker
                  fullWidth
                  value={values.effective_date || ""}
                  onChange={(newValue) => {
                    setFieldValue("effective_date", newValue);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <CustomFormLabel>Year</CustomFormLabel>
                <CustomDatePicker
                  fullWidth
                  views={["year"]}
                  inputFormat="YYYY"
                  value={values.year || ""}
                  onChange={(newValue, time, momentObject) => {
                    const newDate = moment(momentObject).format("YYYY");
                    setFieldValue("year", momentObject ? newDate : null);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3.8}>
                <CustomFormLabel>Serial Number</CustomFormLabel>
                <CustomTextField
                  name="serial_number"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.serial_number}
                  // error={errors.serial_number ? true : false}
                  // helperText={errors.serial_number}
                  // disabled={isUpdateLead}
                  onChange={handleSerialNumberChange}
                />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <CustomFormLabel>Limit ($)</CustomFormLabel>
                <CurrencyTextField
                  fullWidth
                  value={values.limit_amount}
                  onChange={(value) => {
                    setFieldValue("limit_amount", value);
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel>Purchase Date</CustomFormLabel>
                <CustomDatePicker
                  fullWidth
                  value={values.purchase_date || ""}
                  onChange={(newValue) => {
                    setFieldValue("purchase_date", newValue);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InterestedPartyCodeSearchSelect
                  label="Interested Party Code"
                  value={values.interested_party_code}
                  onSelect={(value) => handleIntrestedPartyCode(value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Intersted Party</CustomFormLabel>
                <CustomTextField
                  name="interested_party"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.interested_party}
                  // error={errors.name ? true : false}
                  // helperText={errors.name}
                  // disabled={isUpdateLead}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel>Interest</CustomFormLabel>
                <CustomTextField
                  name="interest"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.interest}
                  // error={errors.name ? true : false}
                  // helperText={errors.name}
                  // disabled={isUpdateLead}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} lg={3}>
                <CustomFormLabel>Address</CustomFormLabel>
                <LocationAutoComplete
                  id="address"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.address}
                  onLocationChange={handleAddress}
                />
              </Grid>
              <Grid item xs={12} lg={1.5}>
                <CustomFormLabel>Unit/Suite</CustomFormLabel>
                <CustomTextField
                  id="unit_or_suite"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.unit_or_suite}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} lg={2}>
                <CustomFormLabel>City</CustomFormLabel>
                <CustomTextField
                  id="city"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} lg={2}>
                <CustomFormLabel>State/Province</CustomFormLabel>
                <CustomTextField
                  id="province_or_state"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.province_or_state}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} lg={1.5}>
                <CustomFormLabel>Postal Code</CustomFormLabel>
                <CustomTextField
                  id="postal_code"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.postal_code}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} lg={2}>
                <CustomFormLabel>Country</CustomFormLabel>
                <CustomTextField
                  id="country"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.country}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Description</CustomFormLabel>
                <CustomTextField
                  name="description"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  multiline
                  rows={3}
                  value={values.description}
                  // error={errors.name ? true : false}
                  // helperText={errors.name}
                  // disabled={isUpdateLead}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Note</CustomFormLabel>
                <CustomTextField
                  name="note"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  multiline
                  rows={3}
                  value={values.note}
                  // error={errors.name ? true : false}
                  // helperText={errors.name}
                  // disabled={isUpdateLead}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} lg={4} sx={{ mt: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    name="equipment_submission_checkbox"
                    size="medium"
                    color="primary"
                    checked={values.equipment_submission_checkbox === "Y"}
                    onChange={(e, checked) => {
                      setFieldValue(
                        "equipment_submission_checkbox",
                        checked ? "Y" : "N"
                      );
                    }}
                  />
                  <CustomFormLabel sx={{ fontSize: "16px" }}>
                    Submission
                  </CustomFormLabel>
                </Stack>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12} lg={4}>
                <CustomFormLabel>Status</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.status}
                  name="status"
                  onChange={handleChange}
                  placeholder="Select one"
                  displayEmpty
                  options={COMMON_STATUS}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <RoleBasedCustomButton
                moduleKey={MODULE_IDS.BUSINESS_POLICY}
                sx={{ width: "15%" }}
                disabled={saveLoading}
                variant="contained"
                type="submit"
              >
                Save
              </RoleBasedCustomButton>
            </Box>
          </FieldSet>
        </form>
      </StandadCard>
    </PageLoader>
    </PageContainer>
  );
};
